/**
 * PATH: packages/ui/src/kanban/components/kanbanCardClassName.ts
 * FILE_ID: UI-KANBAN-CARD-CLASSNAME-001
 * PURPOSE: Compose the Tailwind class list for a Kanban card tile.
 * WHY: Extracted from KanbanCard.tsx during
 *   FIX-2026-07-27-KANBAN-CARD-PRESS-SNAPPY. The tile's class list is the one
 *   place that decides what the card animates, so it earns a directly testable
 *   home instead of being asserted through a source-string pin. Pulling it out
 *   also brings KanbanCard.tsx back under the 500-line ceiling it had already
 *   breached (504 lines on production before this change).
 * FLOW: card + interaction state -> ordered class list -> single joined string.
 * DEPENDENCIES: ../cardColors (CARD_COLOR_SURFACE_CLASS).
 * RELATED: KanbanCard.tsx (sole consumer), KanbanCard.snappiness.pin.test.ts.
 *
 * NOTES FOR FUTURE AI: the transition list is deliberately explicit. Do NOT
 * "simplify" it back to `transition-all` — that pulls this element's own
 * responsive padding utilities into the animated set and makes every hover and
 * drop run a layout animation. Keep every added property compositor- or
 * paint-level (transform, opacity, colour, shadow), never a layout property
 * such as width, padding or margin.
 */
import { CARD_COLOR_SURFACE_CLASS } from '../cardColors'

export interface KanbanCardClassNameInput {
  /** Compact density: tighter vertical padding. */
  isCompact: boolean
  /** Large density. Ignored when `isCompact` is true. */
  isLarge: boolean
  /** Sensitive cards read as a dashed outline. */
  sensitive: boolean
  /** Snoozed cards render dimmed. */
  snoozed: boolean
  /** Pointer is dwelling on the tile (lifts it). */
  hoverActive: boolean
  /** Tile is mid-drag (tilts, shrinks, fades). */
  dragging: boolean
  /** An email is hovering this card as a drop target. */
  emailDropHover: boolean
  /** A card is hovering this card as a nest target. */
  nestDropHover: boolean
}

/**
 * Build the card tile's class string.
 *
 * Pure: same input always yields the same string, no side effects. Order is
 * significant only in that later Tailwind utilities win on conflict, so keep
 * the state-driven modifiers last.
 */
export function buildKanbanCardClassName({
  isCompact,
  isLarge,
  sensitive,
  snoozed,
  hoverActive,
  dragging,
  emailDropHover,
  nestDropHover,
}: KanbanCardClassNameInput): string {
  return [
    // Visual uplift (2026-05-30): rounded-xl tile. ALL elevation + ring
    // feedback is driven by the composed inline `boxShadow` on the element;
    // this className owns only layout, border, transforms and opacity.
    // FIX-2026-07-27-KANBAN-CARD-PRESS-SNAPPY: `transition-all` let the
    // responsive padding utilities animate, so a card could run a layout
    // animation on every frame of a hover or drop. Scoped to the properties
    // this tile actually changes. Generalises FIX-2026-07-02-KANBAN-CARD-ZONE-SNAPPY.
    'group relative cursor-grab rounded-xl border bg-white transition-[transform,opacity,box-shadow,border-color,background-color] duration-200 ease-out',
    // Suppress the iOS long-press callout / selection magnifier so the
    // 500ms touch long-press opens OUR context menu instead of the native
    // text-selection bubble (BUG: "right-click action broken on mobile").
    // No-op on desktop / pointer devices.
    'select-none [-webkit-touch-callout:none]',
    // FEAT-2026-06-22-KANBAN-CARD-COLOR — define the mode-aware surface var
    // the colour tint's color-mix composites over (white / zinc-800).
    CARD_COLOR_SURFACE_CLASS,
    isCompact ? 'py-1 pl-2 pr-2.5' : isLarge ? 'p-3 pr-3.5' : 'p-2 pr-2.5',
    // Press acknowledgement. The drawer this click opens mounts a large
    // subtree, so without a local ack the whole mount cost reads as click
    // latency. 75ms in / 200ms out, and a 1% scale so the quick-action
    // buttons inside the card never move far enough to miss a target.
    'active:cursor-grabbing active:scale-[0.99] active:duration-75',
    'dark:bg-zinc-800',
    'border-neutral-200/80 dark:border-zinc-700/70',
    sensitive ? 'border-dashed' : '',
    snoozed ? 'opacity-60' : '',
    hoverActive ? '-translate-y-0.5' : '',
    dragging ? 'rotate-[1.5deg] scale-[0.98] opacity-60' : '',
    emailDropHover ? 'scale-[1.02]' : '',
    nestDropHover ? 'scale-[1.02]' : '',
  ].join(' ')
}
